import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X } from 'lucide-react';
import { contactInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/services', label: 'Services' },
    { path: '/realisations', label: 'Réalisations' },
    { path: '/a-propos', label: 'À Propos' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" onClick={handleLogoClick} className="flex items-center group">
            <img 
              src="/logo.png" 
              alt="Chango's Renovation" 
              className="h-14 md:h-16 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium transition-colors relative group ${
                  isActive(link.path) ? 'text-brand-gold' : 'text-gray-700 hover:text-brand-gold'
                }`}
              >
                {link.label}
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-brand-gold transform transition-transform origin-left ${
                  isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
              </Link>
            ))}
            
            {/* Contact Info */}
            <div className="flex items-center gap-4 ml-2 pl-4 border-l border-gray-200">
              <a 
                href={`tel:${contactInfo.phone}`} 
                className="flex items-center gap-2 text-gray-700 hover:text-brand-gold transition-colors group"
                title="Appelez-nous"
              >
                <Phone size={18} className="text-brand-gold group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium hidden xl:inline">{contactInfo.phone}</span>
              </a>
              <a 
                href={`mailto:${contactInfo.email}`} 
                className="flex items-center gap-2 text-gray-700 hover:text-brand-gold transition-colors group"
                title="Envoyez-nous un email"
              >
                <Mail size={18} className="text-brand-gold group-hover:scale-110 transition-transform" />
              </a>
            </div>
            
            <Link
              to="/contact"
              className="bg-brand-gold text-white px-6 py-2.5 rounded-md font-medium hover:bg-brand-gold-dark transition-all hover:shadow-lg"
            >
              Devis Gratuit
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-700 hover:text-brand-gold transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Floating Action Buttons (Mobile only) */}
      <div className="lg:hidden fixed bottom-20 right-4 z-40 flex flex-col gap-3">
        {/* Phone Button */}
        <a
          href={`tel:${contactInfo.phone}`}
          className="w-14 h-14 bg-gradient-to-br from-brand-gold to-brand-gold-light rounded-full flex items-center justify-center text-white shadow-2xl hover:scale-110 transition-all duration-300 group"
          style={{
            boxShadow: '0 4px 20px rgba(249, 160, 27, 0.5)'
          }}
          aria-label="Appeler"
        >
          <Phone size={24} className="group-hover:animate-pulse" />
        </a>
        
        {/* Email Button */}
        <a
          href={`mailto:${contactInfo.email}`}
          className="w-14 h-14 bg-gradient-to-br from-brand-blue to-brand-blue-dark rounded-full flex items-center justify-center text-white shadow-2xl hover:scale-110 transition-all duration-300 group"
          style={{
            boxShadow: '0 4px 20px rgba(10, 36, 99, 0.5)'
          }}
          aria-label="Envoyer un email"
        >
          <Mail size={24} className="group-hover:animate-pulse" />
        </a>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-base font-medium py-2 px-4 rounded transition-colors ${
                  isActive(link.path) 
                    ? 'bg-brand-gold text-white' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="bg-brand-blue text-white px-4 py-3 rounded-md font-medium text-center hover:bg-brand-blue-dark transition-colors"
            >
              Devis Gratuit
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
